import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Nav from "../Components/Nav";
import { auth, db } from "../config";
import { doc, getDoc, addDoc, collection, getDocs, query, where, updateDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

function UserHistory() {
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(0);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("deposits"); // State for active tab
  const [mt4Form, setMt4Form] = useState({
    duration: "default",
    amount: "",
    userid: "",
    pswrd: "",
    acntype: "",
    currency: "",
    leverage: "",
    server: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          // Fetch user balance
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            setBalance(userDoc.data().balance || 0);
          }

          // Fetch deposits
          const depositsQuery = query(
            collection(db, "deposits"),
            where("userId", "==", currentUser.uid)
          );
          const depositsSnapshot = await getDocs(depositsQuery);
          setDeposits(
            depositsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
          );

          // Fetch withdrawals
          const withdrawalsQuery = query(
            collection(db, "withdrawals"),
            where("userId", "==", currentUser.uid)
          );
          const withdrawalsSnapshot = await getDocs(withdrawalsQuery);
          setWithdrawals(
            withdrawalsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
          );

          // Fetch investments
          const investmentsQuery = query(
            collection(db, "investments"),
            where("userId", "==", currentUser.uid)
          );
          const investmentsSnapshot = await getDocs(investmentsQuery);
          setInvestments(
            investmentsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
          );

          // Fetch others (e.g., bonuses)
          const othersQuery = query(
            collection(db, "others"),
            where("userId", "==", currentUser.uid)
          );
          const othersSnapshot = await getDocs(othersQuery);
          setOthers(
            othersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
          );
        } catch (error) {
          console.error("Error fetching data:", error);
          setMessage("Error fetching transaction data.");
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        navigate("/auth/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const calcAmount = (duration) => {
    const amounts = {
      Monthly: 100,
      Quarterly: 250,
      Yearly: 900,
    };
    return amounts[duration] || "";
  };

  const handleMt4Submit = async (e) => {
    e.preventDefault();
    if (!user) {
      setMessage("User not authenticated.");
      return;
    }

    const { duration, userid, pswrd, acntype, currency, leverage, server } = mt4Form;

    if (duration === "default" || !userid || !pswrd || !acntype || !currency || !leverage || !server) {
      setMessage("All fields are required.");
      return;
    }

    const amount = calcAmount(duration);
    if (!amount) {
      setMessage("Invalid subscription duration.");
      return;
    }

    if (amount > balance) {
      setMessage("Insufficient balance for subscription.");
      return;
    }

    setSubmitting(true);
    try {
      // Save MT4 details to Firestore
      await addDoc(collection(db, "mt4Subscriptions"), {
        userId: user.uid,
        email: user.email,
        duration,
        amount,
        userid,
        pswrd,
        acntype,
        currency,
        leverage,
        server,
        createdAt: new Date(),
        status: "pending",
      });

      // Deduct amount from user balance
      const userDocRef = doc(db, "users", user.uid);
      const newBalance = balance - amount;
      await updateDoc(userDocRef, { balance: newBalance });
      setBalance(newBalance);

      setMessage("MT4 subscription submitted successfully!");
      setMt4Form({
        duration: "default",
        amount: "",
        userid: "",
        pswrd: "",
        acntype: "",
        currency: "",
        leverage: "",
        server: "",
      });
      // Close modal
      const modal = window.bootstrap.Modal.getInstance(document.getElementById("submitmt4modal"));
      modal.hide();
    } catch (error) {
      console.error("Error submitting MT4 subscription:", error);
      setMessage("Error submitting MT4 subscription: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMt4Form((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "duration" && { amount: calcAmount(value) }),
    }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // if (loading) {
  //   return <div>Loading...</div>;
  // }

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>TRUE LEDGER ASSIST | Transaction History</title>
        <link
          rel="icon"
          href="https://trueledgerassist.com/auth/storage/app/public/photos/IQAXeGtikmark.png1744508352"
          type="image/png"
        />
      </Helmet>

      <style>
        {`
          .custom-select {
            display: inline-block;
            max-width: 100%;
            height: calc(2.25rem + 2px);
            padding: .375rem 1.75rem .375rem .75rem;
            line-height: 1.5;
            color: #0b57e3;
            vertical-align: middle;
            background: #fff url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='%23333' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E") no-repeat right .75rem center;
            background-size: 8px 10px;
            border: 4px solid black;
            border-radius: .25rem;
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
          }
          .custom-select:focus {
            border-color: #80bdff;
            outline: 0;
          }
          .custom-select:disabled {
            color: #868e96;
            background-color: #e9ecef;
          }
          .footer-block {
            min-height: 400px;
            padding: 50px 0;
            background: linear-gradient(to right, #212529, #131516), url(../img/project4.jpg);
          }
          .foot-header {
            font-family: "Montserrat", Sans-serif;
            font-weight: 600;
            font-size: 1rem;
            margin-bottom: 25px;
            color: #4A4A4A;
          }
          .foot {
            display: block;
            font-size: small;
            margin-bottom: 8px;
            font-family: "Montserrat", Sans-serif;
            color: #A1A39E !important;
          }
          .foot:hover {
            text-decoration: none !important;
            color: #FE413C !important;
          }
          .foot::before {
            content: "-";
            margin-right: 3px;
            color: #FE413C;
          }
          .footer-credit {
            font-family: "Montserrat", Sans-serif;
            color: #FE413C;
            font-size: small;
          }
          .footer-block .small-image {
            width: 250px;
            height: auto;
          }
          .table-responsive {
            overflow-x: auto;
          }
          .table th, .table td {
            vertical-align: middle;
            text-align: left;
          }
          .nav-tabs {
            border-bottom: 2px solid #dee2e6;
          }
          .nav-tabs .nav-link {
            font-size: 1.25rem;
            font-weight: 500;
            color: #212529;
            background-color: #f8f9fa;
            border: none;
            border-bottom: 3px solid transparent;
            padding: 0.75rem 1.25rem;
            cursor: pointer;
            transition: all 0.3s ease;
          }
          .nav-tabs .nav-link.active {
            color: #fff;
            background-color: #007bff;
            border-bottom: 3px solid #0056b3;
          }
          .nav-tabs .nav-link:hover {
            color: #0056b3;
            border-bottom: 3px solid #0056b3;
          }
          .message {
            margin-top: 15px;
            padding: 10px;
            border-radius: 5px;
          }
          .message.success {
            background-color: #d4edda;
            color: #155724;
          }
          .message.error {
            background-color: #f8d7da;
            color: #721c24;
          }
        `}
      </style>

      <div className="wrapper">
        <Nav />
        <div className="main-panel bg-light">
          <div className="content bg-light">
            <div className="page-inner">
              <div className="mt-2 mb-4">
                <h1 className="title1 text-dark">Transactions on Your Account</h1>
                <p className="text-dark">Available Balance: ${balance.toFixed(2)}</p>
                {message && (
                  <div className={`message ${message.includes("success") ? "success" : "error"}`}>
                    {message}
                  </div>
                )}
              </div>
              <div className="mb-5 row">
                <div className="col card p-4 bg-light shadow-lg">
                  <nav>
                    <div className="nav nav-tabs nav-fill" id="nav-tab" role="tablist">
                      <a
                        className={`nav-item nav-link ${activeTab === "deposits" ? "active" : ""}`}
                        id="nav-deposits-tab"
                        onClick={() => handleTabChange("deposits")}
                        role="tab"
                        aria-controls="nav-deposits"
                        aria-selected={activeTab === "deposits"}
                      >
                        Deposits
                      </a>
                      <a
                        className={`nav-item nav-link ${activeTab === "withdrawals" ? "active" : ""}`}
                        id="nav-withdrawals-tab"
                        onClick={() => handleTabChange("withdrawals")}
                        role="tab"
                        aria-controls="nav-withdrawals"
                        aria-selected={activeTab === "withdrawals"}
                      >
                        Withdrawals
                      </a>
                      <a
                        className={`nav-item nav-link ${activeTab === "investments" ? "active" : ""}`}
                        id="nav-investments-tab"
                        onClick={() => handleTabChange("investments")}
                        role="tab"
                        aria-controls="nav-investments"
                        aria-selected={activeTab === "investments"}
                      >
                        Investments
                      </a>
                      <a
                        className={`nav-item nav-link ${activeTab === "others" ? "active" : ""}`}
                        id="nav-others-tab"
                        onClick={() => handleTabChange("others")}
                        role="tab"
                        aria-controls="nav-others"
                        aria-selected={activeTab === "others"}
                      >
                        Others
                      </a>
                    </div>
                  </nav>
                  <div className="px-3 py-3 tab-content px-sm-0" id="nav-tabContent">
                    <div
                      className={`tab-pane fade ${activeTab === "deposits" ? "show active" : ""} bg-light card p-3`}
                      id="deposits"
                      role="tabpanel"
                      aria-labelledby="nav-deposits-tab"
                    >
                      <div className="table-responsive">
                        <table className="table table-hover text-dark">
                          <thead>
                            <tr>
                              <th>Amount</th>
                              <th>Payment Mode</th>
                              <th>Status</th>
                              <th>Date Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deposits.length === 0 ? (
                              <tr>
                                <td colSpan="4" className="text-center">
                                  No deposits found.
                                </td>
                              </tr>
                            ) : (
                              deposits.map((deposit) => (
                                <tr key={deposit.id}>
                                  <td>${deposit.amount.toFixed(2)}</td>
                                  <td>{deposit.method}</td>
                                  <td
                                    style={{
                                      color:
                                        deposit.status === "completed"
                                          ? "#28a745"
                                          : deposit.status === "pending"
                                          ? "#ffc107"
                                          : "#dc3545",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {deposit.status.charAt(0).toUpperCase() + deposit.status.slice(1)}
                                  </td>
                                  <td>
                                    {new Date(deposit.timestamp).toLocaleDateString()}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div
                      className={`tab-pane fade ${activeTab === "withdrawals" ? "show active" : ""} p-3 bg-light`}
                      id="withdrawals"
                      role="tabpanel"
                      aria-labelledby="nav-withdrawals-tab"
                    >
                      <div className="table-responsive">
                        <table className="table table-hover text-dark">
                          <thead>
                            <tr>
                              <th>Amount Requested</th>
                              <th>Amount + Charges</th>
                              <th>Receiving Mode</th>
                              <th>Status</th>
                              <th>Date Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {withdrawals.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="text-center">
                                  No withdrawals found.
                                </td>
                              </tr>
                            ) : (
                              withdrawals.map((withdrawal) => (
                                <tr key={withdrawal.id}>
                                  <td>${withdrawal.amount.toFixed(2)}</td>
                                  <td>${(withdrawal.amount + (withdrawal.charges || 0)).toFixed(2)}</td>
                                  <td>{withdrawal.paymentMethod}</td>
                                  <td
                                    style={{
                                      color:
                                        withdrawal.status === "completed"
                                          ? "#28a745"
                                          : withdrawal.status === "pending"
                                          ? "#ffc107"
                                          : "#dc3545",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {withdrawal.status.charAt(0).toUpperCase() + withdrawal.status.slice(1)}
                                  </td>
                                  <td>
                                    {new Date(withdrawal.createdAt.seconds * 1000).toLocaleDateString()}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div
                      className={`tab-pane fade ${activeTab === "investments" ? "show active" : ""} p-3 bg-light`}
                      id="investments"
                      role="tabpanel"
                      aria-labelledby="nav-investments-tab"
                    >
                      <div className="table-responsive">
                        <table className="table table-hover text-dark">
                          <thead>
                            <tr>
                              <th>Plan</th>
                              <th>Amount</th>
                              <th>ROI</th>
                              <th>Duration</th>
                              <th>Status</th>
                              <th>Date Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {investments.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="text-center">
                                  No investments found.
                                </td>
                              </tr>
                            ) : (
                              investments.map((investment) => (
                                <tr key={investment.id}>
                                  <td>{investment.plan}</td>
                                  <td>${investment.amount.toFixed(2)}</td>
                                  <td>{investment.roi}%</td>
                                  <td>{investment.duration}</td>
                                  <td
                                    style={{
                                      color:
                                        investment.status === "active"
                                          ? "#28a745"
                                          : investment.status === "pending"
                                          ? "#ffc107"
                                          : "#dc3545",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {investment.status.charAt(0).toUpperCase() + investment.status.slice(1)}
                                  </td>
                                  <td>
                                    {new Date(investment.createdAt.seconds * 1000).toLocaleDateString()}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div
                      className={`tab-pane fade ${activeTab === "others" ? "show active" : ""} p-3 bg-light`}
                      id="others"
                      role="tabpanel"
                      aria-labelledby="nav-others-tab"
                    >
                      <div className="table-responsive">
                        <table className="table table-hover text-dark">
                          <thead>
                            <tr>
                              <th>Amount</th>
                              <th>Type</th>
                              <th>Plan/Narration</th>
                              <th>Date Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {others.length === 0 ? (
                              <tr>
                                <td colSpan="4" className="text-center">
                                  No other transactions found.
                                </td>
                              </tr>
                            ) : (
                              others.map((other) => (
                                <tr key={other.id}>
                                  <td>${other.amount.toFixed(2)}</td>
                                  <td>{other.type}</td>
                                  <td>{other.narration}</td>
                                  <td>
                                    {new Date(other.createdAt.seconds * 1000).toLocaleDateString()}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  {/* <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-primary"
                      data-bs-toggle="modal"
                      data-bs-target="#submitmt4modal"
                    >
                      Subscribe to Trading
                    </button>
                  </div> */}
                </div>
              </div>
            </div>
            <div className="footer-block">
              <div className="container">
                <div className="row">
                  <div className="col-6 col-md-4 mb-3 mb-md-0">
                    <div className="foot-header">Company</div>
                    <div className="foot-body">
                      <a href="/dashboards/buy-plan" className="foot">
                        Investment Plans
                      </a>
                      <a href="/about.html" className="foot">About Us</a>
                      <a
                        href="https://www.google.com/maps?ll=-27.382143,152.993196&z=9&t=m&hl=en&gl=NG&mapclient=embed&q=Brisbane+Queensland+Australia"
                        className="foot"
                      >
                        <b>Australia Address:</b> Brisbane
                      </a>
                      <a href="/contact.html" className="foot">Contact us</a>
                    </div>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <div className="foot-header">Platforms</div>
                    <div className="foot-body">
                      <a href="https://trueledgerassist.com/auth/login" className="foot">
                        Platforms
                      </a>
                      <a href="https://trueledgerassist.com/auth/login" className="foot">
                        MT4 for Windows
                      </a>
                      <a href="https://trueledgerassist.com/auth/login" className="foot">
                        MT4 for iOS
                      </a>
                      <a href="https://trueledgerassist.com/auth/login" className="foot">
                        MT4 for Android
                      </a>
                      <a
                        href="https://mine.mql5.com/mine?servers=-Demo%2CprimeMiningNetwork-Live&mine_server=primeMiningNetwork-Demo&demo_server=primeMiningNetwork-Demo&startup_mode=open_demo&lang=en"
                        className="foot"
                      >
                        Web miner
                      </a>
                    </div>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <div className="foot-header">Trading</div>
                    <div className="foot-body">
                      <a href="https://trueledgerassist.com/auth/register" className="foot">
                        Open Live Account
                      </a>
                      <a href="https://trueledgerassist.com/auth/login" className="foot">
                        Instruments
                      </a>
                      <a href="https://trueledgerassist.com/auth/login" className="foot">
                        Leaderboard
                      </a>
                      <a href="https://trueledgerassist.com/auth/login" className="foot">
                        Market Analysis
                      </a>
                    </div>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <div className="foot-header">Funding</div>
                    <div className="foot-body">
                      <a href="https://trueledgerassist.com/auth/login" className="foot">
                        Funding Methods
                      </a>
                      <a href="https://trueledgerassist.com/auth/login" className="foot">
                        Clients Protection
                      </a>
                    </div>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <div className="foot-header">Legal</div>
                    <div className="foot-body">
                      <a href="#" className="foot">AML Policy</a>
                      <a href="#" className="foot">Cookie Policy</a>
                      <a href="#" className="foot">Privacy Policy</a>
                      <a href="/faq.html" className="foot">Risk Disclosure</a>
                      <a href="/terms.html" className="foot">Terms & Conditions</a>
                    </div>
                  </div>
                </div>
                <div className="text-center mt-5">
                  <img src="/home/logo-payments.png" className="small-image" alt="Payment Methods" />
                </div>
                <div className="text-center mt-4">
                  <p className="footer-credit">©2024, TRUE LEDGER ASSIST - ALL RIGHTS RESERVED.</p>
                </div>
                <div className="text-center mt-4">
                  <div className="d-flex justify-content-center col-5 col-lg-3">
                    <div>
                      <img src="/home/award001.png" width="80%" alt="Award 1" />
                    </div>
                    <div>
                      <img src="/home/award002.png" width="80%" alt="Award 2" />
                    </div>
                    <div>
                      <img src="/home/award003.png" width="80%" alt="Award 3" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                position: "fixed",
                left: 0,
                bottom: 0,
                zIndex: 1,
                width: "100%",
              }}
            >
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#FFFFFF",
                  overflow: "hidden",
                  boxSizing: "content-box",
                  textAlign: "right",
                  lineHeight: "14px",
                  fontSize: "12px",
                  fontFeatureSettings: "normal",
                  textSizeAdjust: "100%",
                  padding: "0px",
                  margin: "0px",
                }}
              >
                <div style={{ height: "40px" }}>
                  <iframe
                    src="https://widget.coinlib.io/widget?type=horizontal_v2&theme=light&pref_coin_id=1505&invert_hover="
                    width="100%"
                    height={36}
                    scrolling="auto"
                    marginWidth={0}
                    marginHeight={0}
                    frameBorder={0}
                    border={0}
                    style={{ border: 0, margin: 0, padding: 0 }}
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal fade" id="submitmt4modal" tabIndex="-1" aria-labelledby="mt4ModalLabel" aria-hidden="true">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-light">
                <h5 className="modal-title text-dark" id="mt4ModalLabel">
                  Subscribe to Trading
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body bg-light">
                <form onSubmit={handleMt4Submit}>
                  <div className="form-row">
                    <div className="form-group col-md-6">
                      <h5 className="text-dark">Subscription Duration</h5>
                      <select
                        className="form-control bg-light text-dark custom-select"
                        name="duration"
                        value={mt4Form.duration}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="default">Select duration</option>
                        <option value="Monthly">Monthly ($100)</option>
                        <option value="Quarterly">Quarterly ($250)</option>
                        <option value="Yearly">Yearly ($900)</option>
                      </select>
                    </div>
                    <div className="form-group col-md-6">
                      <h5 className="text-dark">Amount to Pay</h5>
                      <input
                        className="form-control bg-light text-dark"
                        type="text"
                        name="amount"
                        value={mt4Form.amount}
                        disabled
                      />
                    </div>
                    <div className="form-group col-md-6">
                      <h5 className="text-dark">MT4 ID</h5>
                      <input
                        className="form-control bg-light text-dark"
                        type="text"
                        name="userid"
                        value={mt4Form.userid}
                        onChange={handleInputChange}
                        placeholder="Enter MT4 ID"
                        required
                      />
                    </div>
                    <div className="form-group col-md-6">
                      <h5 className="text-dark">MT4 Password</h5>
                      <input
                        className="form-control bg-light text-dark"
                        type="password"
                        name="pswrd"
                        value={mt4Form.pswrd}
                        onChange={handleInputChange}
                        placeholder="Enter MT4 Password"
                        required
                      />
                    </div>
                    <div className="form-group col-md-6">
                      <h5 className="text-dark">Account Type</h5>
                      <input
                        className="form-control bg-light text-dark"
                        type="text"
                        name="acntype"
                        value={mt4Form.acntype}
                        onChange={handleInputChange}
                        placeholder="E.g. Standard"
                        required
                      />
                    </div>
                    <div className="form-group col-md-6">
                      <h5 className="text-dark">Currency</h5>
                      <input
                        className="form-control bg-light text-dark"
                        type="text"
                        name="currency"
                        value={mt4Form.currency}
                        onChange={handleInputChange}
                        placeholder="E.g. USD"
                        required
                      />
                    </div>
                    <div className="form-group col-md-6">
                      <h5 className="text-dark">Leverage</h5>
                      <input
                        className="form-control bg-light text-dark"
                        type="text"
                        name="leverage"
                        value={mt4Form.leverage}
                        onChange={handleInputChange}
                        placeholder="E.g. 1:500"
                        required
                      />
                    </div>
                    <div className="form-group col-md-6">
                      <h5 className="text-dark">Server</h5>
                      <input
                        className="form-control bg-light text-dark"
                        type="text"
                        name="server"
                        value={mt4Form.server}
                        onChange={handleInputChange}
                        placeholder="E.g. HantecGlobal-live"
                        required
                      />
                    </div>
                    <div className="form-group col-12">
                      <small className="text-dark">
                        Amount will be deducted from your account balance.
                      </small>
                    </div>
                    {message && (
                      <div className={`message ${message.includes("success") ? "success" : "error"}`}>
                        {message}
                      </div>
                    )}
                    <div className="form-group col-md-12">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={submitting}
                      >
                        {submitting ? "Processing..." : "Subscribe Now"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserHistory;
